import queryString from 'query-string';
import React from 'react';
import {useLocation} from 'react-router-dom';

import {usePushNotification} from 'app/components/Notification';
import {useFeatureData, useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import {useGeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import MapPolygonStateProvider, {WithMapPolygonState} from 'app/providers/MapPolygonStateProvider';
import {WithLoadedOrgUsers} from 'app/providers/OrgUsersProvider';
import {NotesProvider} from 'app/stores/NotesStore';
import * as historyUtils from 'app/utils/historyUtils';
import * as routeUtils from 'app/utils/routeUtils';

import useSceneUrlState from './hooks/useSceneUrlState';
import {useMapState} from './MapStateProvider';
import View, {Props} from './view';

const MonitorProjectViewDataProvider: React.FunctionComponent<
  Omit<
    Props,
    | 'organizationUsers'
    | 'pushNotification'
    | 'notesState'
    | 'notesActions'
    | 'polygonState'
    | 'polygonDispatch'
    | 'mode'
    | 'imageRefs'
    | 'mapStateDispatch'
    | 'showFeatureIdInTitle'
    | 'imageRefFromUrl'
    | 'subsetFeatureBboxFromUrl'
    | 'geoJsonFeaturesLoader'
    | 'featureData'
    | 'getFeatureData'
  >
> = (props) => {
  const location = useLocation();
  const {
    selectedFeature,
    selectedFeatures,
    profile,
    selectedProject,
    selectedFeatureCollection,
    invalidateOrderableScenes,
  } = props;

  const {getFeatureData} = useFeatureData();

  const selectedProjectId = selectedProject.get('id');
  const selectedFeatureCollectionId = selectedFeatureCollection.get('id');
  const selectedFeatureId = selectedFeature && selectedFeature?.get('id');

  React.useEffect(() => {
    //eslint-disable-next-line no-console
    console.log(
      `Selection changed: <project: ${selectedProjectId}> <featureCollection: ${selectedFeatureCollectionId}> <featureIds: ${selectedFeatureId}>`
    );
  }, [selectedProjectId, selectedFeatureCollectionId, selectedFeatureId]);

  // Parse the focused note ID from the URL.
  // Check if there's a focused note in the URL for map initialization
  let initialFocusedNoteId: number | null = null;
  const noteIdMatch = historyUtils.matchInHash(location, routeUtils.NOTE_URL_PARAM_REGEX);

  // First match group is input, second match group is `note-`, third match
  // group is note ID.
  if (noteIdMatch && noteIdMatch.length > 2) {
    initialFocusedNoteId = parseInt(noteIdMatch[2], 10);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [mapState, mapStateDispatch] = useMapState(
    selectedFeatureCollection,
    // useMapState needs the database IDs of the features, so we can't use
    // selectedFeatureIdParams. This delays loading feature data slightly during
    // an initial page load that's a direct link to the feature, since we need
    // to wait for the entire feature collection to load before loading any
    // featuredata.
    selectedFeatures.map((f) => f!.get('id')).toSet(),
    !!initialFocusedNoteId
  );

  const queryParams = React.useMemo(() => queryString.parse(location.search), [location.search]);
  const subsetFeatureBboxFromQueryParams = routeUtils.getStringParamFromQueryParams(
    queryParams,
    routeUtils.SUBSET_FEATURE_BBOX_URL_PARAM
  );

  // TODO(fiona): Could push the StatusMaybe further down, to allow for partial
  // display. But for now all the code assumes that FeatureData is complete when
  // presented, so we don't want to send a partial list.
  const featureData = useFeatureDataForFeature(selectedFeatureId, {
    // We want FeatureData within the last hour.
    afterMs: Date.now() - 1000 * 60 * 60,
    allowStaleMs: true,
  });

  // Owns the scene ↔ URL coupling: deep-link restore on load, and mirroring the
  // on-screen scene(s) into the URL while the internal Alt+I toggle is on. The
  // returned single-scene deep link is consumed by UrlSceneHandler below.
  const imageRefFromUrl = useSceneUrlState({
    featureData,
    imageRefs: mapState.imageRefs,
    mapStateDispatch,
  });

  const geoJsonFeaturesLoader = useGeoJsonFeaturesLoader();

  // The purpose of this is to invlidate orderableScenes once an order is done processing
  // and is ready to view. FeatureData updates in this case so we use that update to trigger
  // the invalidation. This is not a perfect solution and invalidates orderableScenes
  // way more than necesary (e.g. when switching between features). But we don't currently have
  // a cleaner way of doing this.
  React.useEffect(() => {
    if (selectedFeatureId) {
      invalidateOrderableScenes(selectedFeatureId);
    }
  }, [selectedFeatureId, featureData, invalidateOrderableScenes]);

  const pushNotification = usePushNotification();

  return (
    <WithLoadedOrgUsers refreshOnMount>
      {(organizationUsers) => (
        <MapPolygonStateProvider featureId={selectedFeatureId}>
          <WithMapPolygonState>
            {(polygonState, polygonDispatch) => (
              <NotesProvider
                profile={profile}
                organizationUsers={organizationUsers}
                projectId={selectedProject.get('id')}
                featureCollectionId={selectedFeatureCollection.get('id')}
                featureId={selectedFeatureId}
                initialFocusedNoteId={initialFocusedNoteId}
              >
                {(notesState, notesActions) => (
                  <View
                    {...props}
                    organizationUsers={organizationUsers}
                    pushNotification={pushNotification}
                    notesState={notesState}
                    notesActions={notesActions}
                    polygonState={polygonState}
                    polygonDispatch={polygonDispatch}
                    getFeatureData={getFeatureData}
                    featureData={featureData}
                    mode={mapState.mode}
                    imageRefs={mapState.imageRefs}
                    mapStateDispatch={mapStateDispatch}
                    showFeatureIdInTitle={mapState.showFeatureIdInTitle}
                    imageRefFromUrl={imageRefFromUrl}
                    subsetFeatureBboxFromUrl={subsetFeatureBboxFromQueryParams}
                    geoJsonFeaturesLoader={geoJsonFeaturesLoader}
                  />
                )}
              </NotesProvider>
            )}
          </WithMapPolygonState>
        </MapPolygonStateProvider>
      )}
    </WithLoadedOrgUsers>
  );
};

export default MonitorProjectViewDataProvider;
